import nacl from "tweetnacl";
import { deriveViewingKey } from "../joinsplit/keys";
import {
  NoteTokenType,
  type JoinSplitNote,
  type NoteTokenData,
} from "../joinsplit/types";

export const INBOX_ECDH_PREFIX = "inbox-v2:";

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function normalizeHex(hex: string): string {
  return hex.startsWith("0x") ? hex.slice(2) : hex;
}

function bytesToHex(bytes: Uint8Array): string {
  return Buffer.from(bytes).toString("hex");
}

function hexToBytes(hex: string): Uint8Array {
  const cleanHex = normalizeHex(hex);
  if (!cleanHex) return new Uint8Array();
  return new Uint8Array(Buffer.from(cleanHex, "hex"));
}

// The inbox keypair is derived from the viewing key, not the spending key, so
// that inbox read access can be delegated without delegating spend authority.
export function formatInboxPublicKeyHex(spendingKey: Uint8Array): string {
  const keyPair = nacl.box.keyPair.fromSecretKey(deriveViewingKey(spendingKey));
  return `0x${bytesToHex(keyPair.publicKey)}`;
}

/**
 * The inbox public key accounts published before the viewing-key split.
 *
 * Payment links, handle records and every note already sitting in the engine
 * were addressed to this key. It is still handed out to senders nowhere — it
 * exists so this wallet can recognise and open what was sent to it before the
 * key changed.
 */
export function formatLegacyInboxPublicKeyHex(spendingKey: Uint8Array): string {
  const keyPair = nacl.box.keyPair.fromSecretKey(spendingKey);
  return `0x${bytesToHex(keyPair.publicKey)}`;
}

export function getInboxSecretKey(spendingKey: Uint8Array): Uint8Array {
  return nacl.box.keyPair.fromSecretKey(deriveViewingKey(spendingKey))
    .secretKey;
}

/**
 * Every inbox secret this account has ever read with, newest first.
 *
 * Re-keying the inbox off the viewing key does not re-encrypt what is already
 * stored: notes delivered to the pre-split public key are sealed to the
 * spending key's box keypair, and dropping that key would make an account's
 * existing balance silently unreadable rather than merely un-writable. New
 * notes are still written under the current key only — see getInboxSecretKey.
 */
export function getInboxSecretKeyCandidates(
  spendingKey: Uint8Array,
): Uint8Array[] {
  return [
    getInboxSecretKey(spendingKey),
    nacl.box.keyPair.fromSecretKey(spendingKey).secretKey,
  ];
}

/**
 * Every AES viewing key this account's inbox has ever been keyed with, newest
 * first. Same migration as getInboxSecretKeyCandidates, for the pre-ECDH
 * `inbox-v1` records that are keyed directly off the viewing key hex.
 */
export function getInboxViewingKeyCandidates(
  spendingKey: Uint8Array,
): string[] {
  return [bytesToHex(deriveViewingKey(spendingKey)), bytesToHex(spendingKey)];
}

function deriveInboxSharedSecret(
  ephemeralPublicKeyHex: string,
  inboxSecretKey: Uint8Array,
): Uint8Array {
  const publicKey = hexToBytes(ephemeralPublicKeyHex);
  if (publicKey.length !== nacl.box.publicKeyLength) {
    throw new Error("Invalid inbox ephemeral public key");
  }
  return nacl.box.before(publicKey, inboxSecretKey);
}

export function normalizeInboxPublicKeyHex(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const hex = normalizeHex(trimmed);
  if (!/^[0-9a-fA-F]{64}$/.test(hex)) {
    return null;
  }
  return `0x${hex.toLowerCase()}`;
}

export function parsePrivateAddress(
  value: string,
): { mpkHex: string; inboxPublicKeyHex?: string } | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":");
  if (parts.length > 2) return null;
  const mpkHex = normalizeHex(parts[0]);
  if (!/^[0-9a-fA-F]{64}$/.test(mpkHex)) {
    return null;
  }
  const result: { mpkHex: string; inboxPublicKeyHex?: string } = {
    mpkHex: `0x${mpkHex.toLowerCase()}`,
  };
  if (parts.length === 2) {
    const inboxHex = normalizeHex(parts[1]);
    if (!/^[0-9a-fA-F]{64}$/.test(inboxHex)) {
      return null;
    }
    result.inboxPublicKeyHex = `0x${inboxHex.toLowerCase()}`;
  }
  return result;
}

function isNoteTokenData(value: unknown): value is NoteTokenData {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<NoteTokenData>;
  return (
    typeof candidate.tokenAddress === "string" &&
    typeof candidate.tokenSubID === "string" &&
    typeof candidate.tokenType === "number" &&
    Object.values(NoteTokenType).includes(candidate.tokenType as NoteTokenType)
  );
}

async function deriveInboxKey(
  viewingKeyHex: string,
  usage: KeyUsage[],
): Promise<CryptoKey> {
  const keyBytes = Buffer.from(normalizeHex(viewingKeyHex), "hex");
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(new Uint8Array(keyBytes)),
    "HKDF",
    false,
    ["deriveKey"],
  );
  const salt = new TextEncoder().encode("stealthos-inbox-v1");
  const info = new TextEncoder().encode("inbox");
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: toArrayBuffer(salt),
      info: toArrayBuffer(info),
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    usage,
  );
}

async function deriveInboxKeyFromSharedSecret(
  sharedSecret: Uint8Array,
  usage: KeyUsage[],
): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(sharedSecret),
    "HKDF",
    false,
    ["deriveKey"],
  );
  const salt = new TextEncoder().encode("stealthos-inbox-v2");
  const info = new TextEncoder().encode("inbox-ecdh");
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: toArrayBuffer(salt),
      info: toArrayBuffer(info),
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    usage,
  );
}

export async function encryptInboxMessage(
  message: unknown,
  viewingKeyHex: string,
): Promise<{
  ciphertext: string;
  iv: string;
  authTag: string;
  ephemeralPubKey: string;
}> {
  const key = await deriveInboxKey(viewingKeyHex, ["encrypt"]);

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = JSON.stringify(message);

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(new TextEncoder().encode(plaintext)),
  );

  const encryptedBytes = new Uint8Array(encrypted);
  const ciphertext = encryptedBytes.slice(0, -16);
  const authTag = encryptedBytes.slice(-16);

  return {
    ciphertext: Buffer.from(ciphertext).toString("hex"),
    iv: Buffer.from(iv).toString("hex"),
    authTag: Buffer.from(authTag).toString("hex"),
    ephemeralPubKey: "inbox-v1",
  };
}

export async function encryptInboxNoteWithPublicKey(
  note: JoinSplitNote,
  inboxPublicKeyHex: string,
): Promise<{
  ciphertext: string;
  iv: string;
  authTag: string;
  ephemeralPubKey: string;
}> {
  const recipientPubKey = hexToBytes(inboxPublicKeyHex);
  if (recipientPubKey.length !== nacl.box.publicKeyLength) {
    throw new Error("Invalid inbox public key");
  }
  const ephemeral = nacl.box.keyPair();
  const sharedSecret = nacl.box.before(recipientPubKey, ephemeral.secretKey);
  const key = await deriveInboxKeyFromSharedSecret(sharedSecret, ["encrypt"]);

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = JSON.stringify({
    schemaVersion: note.schemaVersion,
    protocol: note.protocol,
    chainId: note.chainId,
    network: note.network,
    chainDomainId: note.chainDomainId,
    poolAddress: note.poolAddress,
    npk: note.npk.toString(),
    random: note.random.toString(),
    value: note.value.toString(),
    commitment: note.commitment,
    tokenHash: note.tokenHash.toString(),
    tokenData: note.tokenData,
    leafIndex: note.leafIndex,
    treeIndex: note.treeIndex,
    treeKey: note.treeKey,
    treePda: note.treePda,
    merkleRoot: note.merkleRoot,
    privateHistory: note.privateHistory,
  });

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(new TextEncoder().encode(plaintext)),
  );

  const encryptedBytes = new Uint8Array(encrypted);
  const ciphertext = encryptedBytes.slice(0, -16);
  const authTag = encryptedBytes.slice(-16);

  return {
    ciphertext: Buffer.from(ciphertext).toString("hex"),
    iv: Buffer.from(iv).toString("hex"),
    authTag: Buffer.from(authTag).toString("hex"),
    ephemeralPubKey: `${INBOX_ECDH_PREFIX}${bytesToHex(ephemeral.publicKey)}`,
  };
}

function toCandidates<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) return [];
  return (Array.isArray(value) ? value : [value]).filter(
    (candidate) => candidate !== undefined && candidate !== null,
  );
}

async function decryptInboxPayload(
  encrypted: {
    ciphertext: string;
    iv: string;
    authTag: string;
    ephemeralPubKey?: string;
  },
  viewingKeyHex: string | string[],
  legacyMpkHex?: string,
  inboxSecretKey?: Uint8Array | Uint8Array[],
): Promise<unknown> {
  const tryDecrypt = async (keyHex: string) => {
    const key = await deriveInboxKey(keyHex, ["decrypt"]);

    const ciphertext = Buffer.from(encrypted.ciphertext, "hex");
    const authTag = Buffer.from(encrypted.authTag, "hex");
    const combined = new Uint8Array(ciphertext.length + authTag.length);
    combined.set(ciphertext, 0);
    combined.set(authTag, ciphertext.length);

    const ivBytes = Buffer.from(encrypted.iv, "hex");
    return crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(ivBytes) },
      key,
      toArrayBuffer(combined),
    );
  };

  const tryDecryptWithSharedSecret = async (sharedSecret: Uint8Array) => {
    const key = await deriveInboxKeyFromSharedSecret(sharedSecret, ["decrypt"]);
    const ciphertext = Buffer.from(encrypted.ciphertext, "hex");
    const authTag = Buffer.from(encrypted.authTag, "hex");
    const combined = new Uint8Array(ciphertext.length + authTag.length);
    combined.set(ciphertext, 0);
    combined.set(authTag, ciphertext.length);

    const ivBytes = Buffer.from(encrypted.iv, "hex");
    return crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(ivBytes) },
      key,
      toArrayBuffer(combined),
    );
  };

  // Each key this account has been addressed under is tried in turn, newest
  // first. AES-GCM authenticates, so a wrong key fails rather than returning
  // junk, and only an exhausted list is a real decryption failure.
  const attempt = async <K>(
    candidates: K[],
    open: (candidate: K) => Promise<ArrayBuffer>,
  ): Promise<ArrayBuffer> => {
    let lastError: unknown;
    for (const candidate of candidates) {
      try {
        return await open(candidate);
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new Error("Inbox record could not be decrypted with any known key");
  };

  let decrypted: ArrayBuffer;
  const encryptedKey = encrypted.ephemeralPubKey || "";
  if (encryptedKey.startsWith(INBOX_ECDH_PREFIX)) {
    const secretKeys = toCandidates(inboxSecretKey);
    if (secretKeys.length === 0) {
      throw new Error("Inbox secret key unavailable");
    }
    const publicKeyHex = encryptedKey.slice(INBOX_ECDH_PREFIX.length);
    decrypted = await attempt(secretKeys, (secretKey) =>
      tryDecryptWithSharedSecret(
        deriveInboxSharedSecret(publicKeyHex, secretKey),
      ),
    );
  } else {
    const viewingKeys = toCandidates(viewingKeyHex);
    if (legacyMpkHex) {
      viewingKeys.push(legacyMpkHex);
    }
    decrypted = await attempt(viewingKeys, tryDecrypt);
  }

  return JSON.parse(new TextDecoder().decode(decrypted));
}

export async function encryptInboxMessageWithPublicKey(
  message: unknown,
  inboxPublicKeyHex: string,
): Promise<{
  ciphertext: string;
  iv: string;
  authTag: string;
  ephemeralPubKey: string;
}> {
  const recipientPubKey = hexToBytes(inboxPublicKeyHex);
  if (recipientPubKey.length !== nacl.box.publicKeyLength) {
    throw new Error("Invalid inbox public key");
  }
  const ephemeral = nacl.box.keyPair();
  const sharedSecret = nacl.box.before(recipientPubKey, ephemeral.secretKey);
  const key = await deriveInboxKeyFromSharedSecret(sharedSecret, ["encrypt"]);

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = JSON.stringify(message);

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(new TextEncoder().encode(plaintext)),
  );

  const encryptedBytes = new Uint8Array(encrypted);
  const ciphertext = encryptedBytes.slice(0, -16);
  const authTag = encryptedBytes.slice(-16);

  return {
    ciphertext: Buffer.from(ciphertext).toString("hex"),
    iv: Buffer.from(iv).toString("hex"),
    authTag: Buffer.from(authTag).toString("hex"),
    ephemeralPubKey: `${INBOX_ECDH_PREFIX}${bytesToHex(ephemeral.publicKey)}`,
  };
}

export async function decryptInboxMessage<T>(
  encrypted: {
    ciphertext: string;
    iv: string;
    authTag: string;
    ephemeralPubKey?: string;
  },
  viewingKeyHex: string | string[],
  legacyMpkHex?: string,
  inboxSecretKey?: Uint8Array | Uint8Array[],
): Promise<T> {
  return decryptInboxPayload(
    encrypted,
    viewingKeyHex,
    legacyMpkHex,
    inboxSecretKey,
  ) as Promise<T>;
}

export async function decryptInboxNote(
  encrypted: {
    ciphertext: string;
    iv: string;
    authTag: string;
    token?: string;
    commitment?: string;
    ephemeralPubKey?: string;
    leafIndex?: number;
    leaf_index?: number;
    treeIndex?: number;
    tree_index?: number;
    treeKey?: string;
    tree_key?: string;
    treePda?: string;
    tree_pda?: string;
    merkleRoot?: string;
    merkle_root?: string;
  },
  viewingKeyHex: string | string[],
  legacyMpkHex?: string,
  inboxSecretKey?: Uint8Array | Uint8Array[],
): Promise<JoinSplitNote> {
  const plaintext = await decryptInboxMessage<{
    schemaVersion?: number;
    protocol?: string;
    chainId?: number;
    network?: string;
    chainDomainId?: string;
    poolAddress?: string;
    npk: string;
    random: string;
    value: string;
    commitment?: string;
    tokenHash?: string;
    tokenData?: unknown;
    leafIndex?: number;
    treeIndex?: number;
    treeKey?: string;
    treePda?: string;
    merkleRoot?: string;
    privateHistory?: unknown;
  }>(encrypted, viewingKeyHex, legacyMpkHex, inboxSecretKey);
  const rawCommitment = plaintext.commitment ?? encrypted.commitment ?? "";
  const commitment = rawCommitment.startsWith("0x")
    ? rawCommitment
    : `0x${rawCommitment}`;
  const leafIndex =
    plaintext.leafIndex ?? encrypted.leafIndex ?? encrypted.leaf_index ?? -1;
  const treeIndex =
    plaintext.treeIndex ?? encrypted.treeIndex ?? encrypted.tree_index;
  const treeKey = plaintext.treeKey ?? encrypted.treeKey ?? encrypted.tree_key;
  const treePda = plaintext.treePda ?? encrypted.treePda ?? encrypted.tree_pda;
  const merkleRoot =
    plaintext.merkleRoot ?? encrypted.merkleRoot ?? encrypted.merkle_root;

  return {
    schemaVersion:
      plaintext.schemaVersion === 2
        ? (plaintext.schemaVersion as 2)
        : undefined,
    protocol:
      plaintext.protocol === "flat-joinsplit-v2"
        ? "flat-joinsplit-v2"
        : undefined,
    chainId:
      Number.isSafeInteger(plaintext.chainId) && Number(plaintext.chainId) > 0
        ? Number(plaintext.chainId)
        : undefined,
    network:
      typeof plaintext.network === "string" ? plaintext.network : undefined,
    chainDomainId:
      typeof plaintext.chainDomainId === "string"
        ? plaintext.chainDomainId
        : undefined,
    poolAddress:
      typeof plaintext.poolAddress === "string"
        ? plaintext.poolAddress
        : undefined,
    npk: BigInt(plaintext.npk),
    random: BigInt(plaintext.random),
    value: BigInt(plaintext.value),
    tokenHash: BigInt(plaintext.tokenHash ?? encrypted.token ?? "0"),
    tokenData: isNoteTokenData(plaintext.tokenData)
      ? plaintext.tokenData
      : undefined,
    commitment,
    leafIndex,
    treeIndex,
    treeKey,
    treePda,
    merkleRoot,
    privateHistory: isPrivateHistoryMetadata(plaintext.privateHistory)
      ? plaintext.privateHistory
      : undefined,
  };
}

function isPrivateHistoryMetadata(
  value: unknown,
): value is NonNullable<JoinSplitNote["privateHistory"]> {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  if (
    record.type !== undefined &&
    !["deposit", "transfer", "withdraw", "receive", "swap"].includes(
      String(record.type),
    )
  ) {
    return false;
  }
  for (const key of ["amount", "symbol", "memo", "txHash"]) {
    if (record[key] !== undefined && typeof record[key] !== "string") {
      return false;
    }
  }
  return record.timestamp === undefined || typeof record.timestamp === "number";
}
